import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Bell, MessageSquare, Clock, User, AlertCircle, X, Check, DollarSign, Star, Calendar, Loader2, Filter, CheckCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { playNotificationSound } from '../utils/notificationSound';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Notification types with icons, colors, and navigation paths
const NOTIFICATION_TYPES = {
  message: { icon: MessageSquare, color: 'bg-blue-100 text-blue-700', label: 'Messaggio', path: '/dashboard/patient?tab=messages' },
  timer: { icon: Clock, color: 'bg-amber-100 text-amber-700', label: 'Timer', path: '/dashboard/doctor' },
  appointment: { icon: User, color: 'bg-emerald-100 text-emerald-700', label: 'Appuntamento', path: '/admin?tab=appointments' },
  appointment_confirmed: { icon: Check, color: 'bg-emerald-100 text-emerald-700', label: 'Confermato', path: '/admin?tab=appointments' },
  appointment_declined: { icon: AlertCircle, color: 'bg-red-100 text-red-700', label: 'Rifiutato', path: '/admin?tab=appointments' },
  appointment_reminder: { icon: Clock, color: 'bg-amber-100 text-amber-700', label: 'Promemoria', path: '/admin?tab=appointments' },
  new_booking: { icon: Calendar, color: 'bg-emerald-100 text-emerald-700', label: 'Prenotazione', path: '/admin?tab=appointments' },
  payroll_created: { icon: DollarSign, color: 'bg-sky-100 text-sky-700', label: 'Cedolino', path: '/dashboard/doctor?tab=payroll' },
  payroll_paid: { icon: Check, color: 'bg-emerald-100 text-emerald-700', label: 'Pagamento', path: '/dashboard/doctor?tab=payroll' },
  payroll_due_alert: { icon: AlertCircle, color: 'bg-amber-100 text-amber-700', label: 'Scadenza', path: '/dashboard/doctor?tab=payroll' },
  payroll_overdue_alert: { icon: AlertCircle, color: 'bg-red-100 text-red-700', label: 'Scaduto', path: '/dashboard/doctor?tab=payroll' },
  payment_received: { icon: DollarSign, color: 'bg-emerald-100 text-emerald-700', label: 'Pagamento', path: '/admin?tab=invoices' },
  invoice_created: { icon: DollarSign, color: 'bg-sky-100 text-sky-700', label: 'Fattura', path: '/admin?tab=invoices' },
  review_request: { icon: Star, color: 'bg-purple-100 text-purple-700', label: 'Recensione', path: '/dashboard/patient' },
  shift_assigned: { icon: Calendar, color: 'bg-blue-100 text-blue-700', label: 'Turno', path: '/dashboard/doctor?tab=turni' },
  patient_ready: { icon: User, color: 'bg-emerald-100 text-emerald-700', label: 'Paziente Pronto', path: '/dashboard/doctor' },
  patient_called: { icon: Bell, color: 'bg-sky-100 text-sky-700', label: 'Chiamata', path: '/dashboard/doctor' },
  checkin_complete: { icon: Check, color: 'bg-emerald-100 text-emerald-700', label: 'Check-in', path: '/admin?tab=appointments' },
  payment_success: { icon: Check, color: 'bg-emerald-100 text-emerald-700', label: 'Pagamento', path: '/admin?tab=invoices' },
  new_patient: { icon: User, color: 'bg-blue-100 text-blue-700', label: 'Nuovo Paziente', path: '/admin?tab=patients' },
  system: { icon: AlertCircle, color: 'bg-purple-100 text-purple-700', label: 'Sistema', path: '/dashboard/patient' },
  videoconsult: { icon: Calendar, color: 'bg-violet-100 text-violet-700', label: 'Videoconsulto', path: '/dashboard/patient?tab=telemedicine' },
};

export const NotificationBell = ({ userId, iconColor = '#2A9FCB' }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [showAllModal, setShowAllModal] = useState(false);
  const [allNotifications, setAllNotifications] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Request browser notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Load notifications
  useEffect(() => {
    loadNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [userId]);

  // Close dropdown when clicking outside (but not when modal is open)
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if modal is open
      if (showAllModal) return;
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAllModal]);

  const loadNotifications = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newNotifications = response.data.notifications || [];
      const newUnreadCount = response.data.unread_count || 0;
      
      // Check for new important notifications to show browser notification
      if (newUnreadCount > unreadCount && unreadCount >= 0) {
        // Play sound for new notifications
        playNotificationSound();
        
        if ('Notification' in window && Notification.permission === 'granted') {
          const latestUnread = newNotifications.find(n => !n.read);
          if (latestUnread && ['patient_ready', 'checkin_complete', 'appointment_confirmed', 'patient_called', 'message', 'payment_received'].includes(latestUnread.type)) {
            new Notification(latestUnread.title || 'PRIME VISION', {
              body: latestUnread.message,
              icon: '/logo-primevision-small.png',
              tag: latestUnread.id
            });
          }
        }
      }
      
      setNotifications(newNotifications);
      setUnreadCount(newUnreadCount);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/notifications/mark-all-read`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('Tutte le notifiche segnate come lette');
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Navigate to the relevant area based on notification type
  const handleNotificationClick = (notification) => {
    // Mark as read first
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    // Get user role for smart navigation
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isAdmin = user.role === 'admin';
    const isDoctor = user.role === 'doctor' || user.role === 'ortottista';
    
    // Get the base path from notification type config
    const typeConfig = NOTIFICATION_TYPES[notification.type] || NOTIFICATION_TYPES.system;
    let targetPath = typeConfig.path || '/dashboard/patient';
    
    // PRIORITY 1: If notification has a specific link in data, use that
    if (notification.data?.link) {
      targetPath = notification.data.link;
      navigate(targetPath);
      setIsOpen(false);
      return;
    }
    
    // PRIORITY 2: If notification has a direct link property, use that
    if (notification.link) {
      targetPath = notification.link;
      navigate(targetPath);
      setIsOpen(false);
      return;
    }
    
    // Smart navigation based on notification type and data
    const notifType = notification.type?.toLowerCase() || '';
    const notifMessage = (notification.message || '').toLowerCase();
    
    // Payment/Invoice notifications → Fatture (with specific invoice if available)
    if (notifType.includes('payment') || notifType.includes('invoice') || 
        notifType.includes('pagamento') || notifMessage.includes('pagamento') ||
        notifMessage.includes('fattura')) {
      if (notification.data?.invoice_id && isAdmin) {
        targetPath = `/admin?tab=invoices&invoice=${notification.data.invoice_id}`;
      } else {
        targetPath = isAdmin ? '/admin?tab=invoices' : '/dashboard/patient?tab=payments';
      }
    }
    
    // Appointment/Booking notifications → Calendario appuntamenti
    if (notifType.includes('appointment') || notifType.includes('booking') ||
        notifType.includes('prenotazione') || notifMessage.includes('prenotato') ||
        notifMessage.includes('appuntamento') || notifMessage.includes('prenotazione')) {
      targetPath = isAdmin ? '/admin?tab=appointments' : 
                   isDoctor ? '/dashboard/doctor?tab=agenda' : '/dashboard/patient?tab=appointments';
    }
    
    // Shift/Turn notifications → Turni (for doctors)
    if (notifType.includes('shift') || notifType.includes('turno') || notifType.includes('conflict') ||
        notifMessage.includes('turno') || notifMessage.includes('richiesta turno') ||
        notifMessage.includes('conferma turno') || notifMessage.includes('disponibilità') ||
        notifMessage.includes('indisponibilità') || notifMessage.includes('conflitto')) {
      if (isDoctor) {
        targetPath = '/dashboard/doctor?tab=turni';
      } else if (isAdmin) {
        // Se c'è un shift_id nel data della notifica, aggiungi come parametro per aprire il modal
        if (notification.shift_id) {
          targetPath = `/admin?tab=shifts&conflict_shift_id=${notification.shift_id}`;
        } else if (notification.data?.shift_id) {
          targetPath = `/admin?tab=shifts&conflict_shift_id=${notification.data.shift_id}`;
        } else {
          targetPath = '/admin?tab=shifts';
        }
      }
    }
    
    // New patient notifications → Lista pazienti
    if (notifType.includes('new_patient') || notifType.includes('nuovo_paziente') ||
        notifMessage.includes('nuovo paziente') || notifMessage.includes('registrato')) {
      targetPath = isAdmin ? '/admin?tab=patients' : targetPath;
    }
    
    // Check-in notifications → Appuntamenti
    if (notifType.includes('checkin') || notifMessage.includes('check-in')) {
      targetPath = isAdmin ? '/admin?tab=appointments' : targetPath;
    }
    
    // Message notifications
    if (notifType === 'message' || notifMessage.includes('messaggio')) {
      targetPath = isAdmin ? '/admin?tab=messages' : '/dashboard/patient?tab=messages';
    }
    
    // If notification has specific IDs, append them for deep linking
    if (notification.data?.patient_id && isAdmin) {
      targetPath = `/cartella-clinica/${notification.data.patient_id}`;
    }
    if (notification.data?.invoice_id && isAdmin) {
      targetPath = `/admin?tab=invoices&invoice=${notification.data.invoice_id}`;
    }
    if (notification.data?.appointment_id) {
      if (isAdmin) {
        targetPath = `/admin?tab=appointments&date=${notification.data.appointment_date || ''}`;
      } else {
        targetPath = `/dashboard/patient?tab=appointments&id=${notification.data.appointment_id}`;
      }
    }
    
    // Close dropdown and navigate
    setIsOpen(false);
    navigate(targetPath);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = (now - date) / 1000; // seconds

    if (diff < 60) return 'Ora';
    if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ore fa`;
    return date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
  };

  // Load all notifications for modal
  const loadAllNotifications = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setLoadingAll(true);
    try {
      const response = await axios.get(`${API}/notifications?limit=500`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Error loading all notifications:', error);
      toast.error('Errore nel caricamento delle notifiche');
    } finally {
      setLoadingAll(false);
    }
  };

  // Open the "View All" modal
  const openAllNotificationsModal = () => {
    setIsOpen(false); // Close dropdown
    setShowAllModal(true);
    loadAllNotifications();
  };

  // Handle notification click from the modal
  const handleModalNotificationClick = (notification) => {
    setShowAllModal(false);
    handleNotificationClick(notification);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
      >
        <Bell className="w-6 h-6" style={{ color: iconColor }} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown / Modal - Portal */}
      {isOpen && ReactDOM.createPortal(
        <>
          {/* Overlay */}
          <div className="fixed inset-0 bg-black/40 z-[9998]" onClick={() => setIsOpen(false)} />
          {/* Panel */}
          <div className="fixed inset-4 sm:inset-auto sm:top-16 sm:right-4 sm:left-auto sm:w-96 sm:max-h-[80vh] bg-white rounded-2xl shadow-2xl border z-[9999] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 border-b flex items-center justify-between shrink-0" style={{ background: '#F9FAFB' }}>
              <h3 className="font-bold text-lg" style={{ color: '#2A9FCB' }}>Notifiche</h3>
              <div className="flex items-center gap-3">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs font-medium hover:underline"
                    style={{ color: '#2A9FCB' }}
                  >
                    Segna tutte come lette
                  </button>
                )}
                <button onClick={() => setIsOpen(false)} className="p-2 rounded-full hover:bg-gray-200 transition-colors" data-testid="close-notifications-btn">
                  <X className="w-6 h-6 text-gray-600" />
                </button>
              </div>
            </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <Bell className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Nessuna notifica</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const typeConfig = NOTIFICATION_TYPES[notification.type] || NOTIFICATION_TYPES.system;
                const Icon = typeConfig.icon;
                
                return (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`px-4 py-3 border-b hover:bg-gray-100 cursor-pointer transition-all group ${
                      !notification.read ? 'bg-blue-50/50' : ''
                    }`}
                    title="Clicca per vedere i dettagli"
                  >
                    <div className="flex gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${typeConfig.color} group-hover:scale-110 transition-transform`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!notification.read ? 'font-semibold' : ''} text-gray-800 group-hover:text-[#2A9FCB]`}>
                          {notification.title}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-xs text-gray-400">
                            {formatTime(notification.created_at)}
                          </p>
                          <span className="text-xs text-[#2A9FCB] opacity-0 group-hover:opacity-100 transition-opacity">
                            → Vai
                          </span>
                        </div>
                      </div>
                      {!notification.read && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 border-t text-center shrink-0" style={{ background: '#F9FAFB' }}>
              <button 
                onClick={openAllNotificationsModal}
                className="text-xs font-medium hover:underline" 
                style={{ color: '#2A9FCB' }}
                data-testid="view-all-notifications-btn"
              >
                Vedi tutte le notifiche
              </button>
            </div>
          )}
        </div>
        </>
      , document.body)}

      {/* Modal: Tutte le Notifiche - Renderizzato con Portal fuori dalla gerarchia DOM */}
      {showAllModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" onClick={() => setShowAllModal(false)}>
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="all-notifications-modal"
          >
            {/* Modal Header */}
            <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-[#2A9FCB] to-[#1e7a9c]">
              <div className="flex items-center gap-3">
                <Bell className="w-6 h-6 text-white" />
                <h2 className="text-lg font-bold text-white">Tutte le Notifiche</h2>
                <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">
                  {allNotifications.length}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {allNotifications.some(n => !n.read) && (
                  <button
                    onClick={async () => {
                      await markAllAsRead();
                      setAllNotifications(prev => prev.map(n => ({ ...n, read: true })));
                    }}
                    className="text-white/80 hover:text-white text-xs flex items-center gap-1 px-2 py-1 rounded hover:bg-white/10 transition-colors"
                  >
                    <CheckCheck className="w-4 h-4" />
                    Segna tutte lette
                  </button>
                )}
                <button 
                  onClick={() => setShowAllModal(false)}
                  className="text-white/80 hover:text-white p-1 rounded hover:bg-white/10 transition-colors"
                  data-testid="close-notifications-modal-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto">
              {loadingAll ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-[#2A9FCB] animate-spin" />
                  <span className="ml-3 text-gray-500">Caricamento notifiche...</span>
                </div>
              ) : allNotifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <Bell className="w-16 h-16 mb-4 opacity-30" />
                  <p className="text-lg font-medium">Nessuna notifica</p>
                  <p className="text-sm">Non hai ancora ricevuto notifiche</p>
                </div>
              ) : (
                <div className="divide-y">
                  {allNotifications.map((notification) => {
                    const typeConfig = NOTIFICATION_TYPES[notification.type] || NOTIFICATION_TYPES.system;
                    const Icon = typeConfig.icon;
                    
                    return (
                      <div
                        key={notification.id}
                        onClick={() => handleModalNotificationClick(notification)}
                        className={`px-6 py-4 hover:bg-gray-50 cursor-pointer transition-all group ${
                          !notification.read ? 'bg-blue-50/30' : ''
                        }`}
                        data-testid={`notification-item-${notification.id}`}
                      >
                        <div className="flex gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${typeConfig.color} group-hover:scale-105 transition-transform`}>
                            <Icon className="w-6 h-6" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <p className={`text-sm ${!notification.read ? 'font-semibold text-gray-900' : 'text-gray-700'} group-hover:text-[#2A9FCB]`}>
                                {notification.title}
                              </p>
                              <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${typeConfig.color}`}>
                                {typeConfig.label}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                              {notification.message}
                            </p>
                            <div className="flex items-center gap-3 mt-2">
                              <p className="text-xs text-gray-400">
                                {formatTime(notification.created_at)}
                              </p>
                              {!notification.read && (
                                <span className="flex items-center gap-1 text-xs text-blue-500">
                                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                  Non letta
                                </span>
                              )}
                              <span className="text-xs text-[#2A9FCB] opacity-0 group-hover:opacity-100 transition-opacity ml-auto">
                                Clicca per aprire →
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            {allNotifications.length > 0 && (
              <div className="px-6 py-3 border-t bg-gray-50 text-center text-xs text-gray-500">
                Mostrando {allNotifications.length} notifiche • Clicca su una notifica per vedere i dettagli
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// Function to send browser notification (can be called from anywhere)
export const sendBrowserNotification = (title, body, options = {}) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, {
      body,
      icon: '/favicon.ico',
      tag: options.tag || 'general',
      requireInteraction: options.requireInteraction || false,
      ...options
    });
  }
};

export default NotificationBell;
